/**
 * D24 -- Company Announcements page + reusable Home widget.
 *
 * Two exports:
 *   * default `AnnouncementsPage` -- full list route at /announcements.
 *     Filters by category, "Create" dialog for CEO/ADMIN/HR_MANAGER.
 *   * named `AnnouncementsHomeWidget` -- top-3 for Home dashboard.
 *
 * RBAC: read visibility is server-enforced (audience filter in the
 * route); create-button visibility mirrors the RBAC matrix key
 * `announcement_create`.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Pin, Plus, X, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/store/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import errorToast from "@/lib/errorToast";

const CATS = ["general", "policy", "celebration", "urgent", "hr"];
const CAT_STYLE = {
  general: "bg-white/10 text-foreground/85 border-white/15",
  policy: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  celebration: "bg-purple-500/15 text-purple-200 border-purple-500/40",
  urgent: "bg-rose-500/15 text-rose-200 border-rose-500/40",
  hr: "bg-sky-500/15 text-sky-200 border-sky-500/40",
};

function canBroadcast(user) {
  const r = (user?.role || "").toUpperCase();
  return ["CEO", "ADMIN", "HR_MANAGER"].includes(r);
}

export default function AnnouncementsPage() {
  const me = useAuth((s) => s.user);
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  const [openCreate, setOpenCreate] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = cat ? { category: cat } : {};
      const { data } = await api.get("/announcements/", { params });
      setItems(data.items || []);
    } catch (e) { errorToast(e); }
  }, [cat]);

  useEffect(() => { load(); }, [load]);

  const ack = async (id) => {
    try {
      await api.post(`/announcements/${id}/acknowledge`);
      toast.success("Marked as read");
      load();
    } catch (e) { errorToast(e); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await api.delete(`/announcements/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { errorToast(e); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="announcements-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-sky-300" />
          <h1 className="font-heading text-xl tracking-widest">ANNOUNCEMENTS</h1>
        </div>
        {canBroadcast(me) && (
          <Button onClick={() => setOpenCreate(true)}
                  className="bg-gradient-primary text-white gap-2"
                  data-testid="ann-create-btn">
            <Plus className="h-4 w-4" /> New announcement
          </Button>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant={cat === "" ? "default" : "outline"}
                onClick={() => setCat("")}>All</Button>
        {CATS.map((c) => (
          <Button key={c} size="sm" variant={cat === c ? "default" : "outline"}
                  onClick={() => setCat(c)}
                  className="capitalize">{c}</Button>
        ))}
      </div>
      <div className="space-y-2">
        {items.length === 0 ? (
          <div className="text-sm text-foreground/55">No announcements yet.</div>
        ) : items.map((a) => (
          <div key={a.id}
               className="rounded-lg border border-white/10 bg-white/[0.03] p-4"
               data-testid={`ann-row-${a.id}`}>
            <div className="flex items-start gap-2">
              {a.pinned && <Pin className="h-4 w-4 text-sky-300 mt-1" />}
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium">{a.title}</div>
                  <Badge variant="outline" className={CAT_STYLE[a.category] || CAT_STYLE.general}>
                    {a.category}
                  </Badge>
                  {(a.acknowledged_by || []).includes(me?.id) && (
                    <Badge variant="outline" className="bg-emerald-500/10 text-emerald-300 border-emerald-500/40 text-[10px]">
                      <Check className="h-2.5 w-2.5 mr-0.5" /> Read
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-foreground/80 mt-1 whitespace-pre-wrap">{a.body}</div>
                <div className="text-[11px] text-foreground/50 mt-2 flex items-center gap-2 flex-wrap">
                  <span>by {a.created_by_name}</span>
                  <span>·</span>
                  <span>{a.created_at?.slice(0, 10)}</span>
                  {(a.acknowledged_by || []).length > 0 && (
                    <>
                      <span>·</span>
                      <span>{a.acknowledged_by.length} acknowledged</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!(a.acknowledged_by || []).includes(me?.id) && (
                  <Button size="sm" variant="ghost" onClick={() => ack(a.id)}
                          data-testid={`ann-ack-${a.id}`}>
                    Mark read
                  </Button>
                )}
                {(a.created_by === me?.id || (me?.role || "").toUpperCase() === "CEO") && (
                  <Button size="sm" variant="ghost" onClick={() => del(a.id)}
                          data-testid={`ann-del-${a.id}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <CreateAnnouncementDialog open={openCreate} onClose={() => setOpenCreate(false)} onSaved={load} />
    </div>
  );
}

function CreateAnnouncementDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "", body: "", category: "general", pinned: false,
    push_notify: true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (!form.body.trim()) { toast.error("Body required"); return; }
    setSaving(true);
    try {
      await api.post("/announcements/", {
        ...form, audience: { kind: "all", ids: [] }, attachments: [],
      });
      toast.success("Announcement broadcast");
      onSaved(); onClose();
      setForm({ title: "", body: "", category: "general", pinned: false, push_notify: true });
    } catch (e) { errorToast(e); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ann-create-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>New announcement</DialogTitle>
          <DialogDescription>Broadcasts to everyone by default. Push notification fires if enabled.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Title" value={form.title}
                 onChange={(e) => setForm({ ...form, title: e.target.value })}
                 maxLength={120} className="bg-white/5"
                 data-testid="ann-create-title" />
          <Textarea placeholder="Body" value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    className="bg-white/5 min-h-[120px]"
                    data-testid="ann-create-body" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="h-9 rounded-md bg-white/5 border border-white/10 px-2 text-sm capitalize"
                    data-testid="ann-create-cat">
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.pinned}
                     onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
                     data-testid="ann-create-pinned" />
              Pin to top
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.push_notify}
                   onChange={(e) => setForm({ ...form, push_notify: e.target.checked })}
                   data-testid="ann-create-push" />
            Send push notification to everyone
          </label>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button className="bg-gradient-primary text-white"
                    disabled={saving} onClick={submit}
                    data-testid="ann-create-submit">
              {saving ? "Broadcasting..." : "Broadcast"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


export function AnnouncementsHomeWidget() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/announcements/home?limit=3").then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, []);
  if (items.length === 0) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
         data-testid="announcements-widget">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-foreground/55">
          <Megaphone className="h-3 w-3" /> Announcements
        </div>
        <Link to="/announcements" className="text-[11px] text-sky-300 hover:underline">
          See all
        </Link>
      </div>
      <div className="space-y-2">
        {items.map((a) => (
          <Link key={a.id} to="/announcements"
                className="block rounded-md p-2 bg-white/[0.03] hover:bg-white/[0.06] transition"
                data-testid={`ann-widget-${a.id}`}>
            <div className="flex items-center gap-1.5">
              {a.pinned && <Pin className="h-3 w-3 text-sky-300" />}
              <div className="text-sm truncate flex-1">{a.title}</div>
              <Badge variant="outline" className={`${CAT_STYLE[a.category] || CAT_STYLE.general} text-[9px]`}>
                {a.category}
              </Badge>
            </div>
            <div className="text-[11px] text-foreground/55 truncate mt-0.5">{a.body}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
